import React, { useState, useMemo } from 'react';
import { Event } from '@/lib/types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Search, Edit, Trash2, Eye, MoreHorizontal, ArrowUpDown } from 'lucide-react';
import { getEventDateRangeString } from '@/lib/eventHelpers';
import { eventTypeLabels } from '@/lib/eventLabels';
import StatusBadge from './StatusBadge';
import { EventStatus } from '@/services/eventService';

interface EnhancedEventsTableProps {
  events: Event[];
  onViewEvent: (event: Event) => void;
  onEditEvent: (event: Event) => void;
  onDeleteEvent: (event: Event) => void;
}

type SortField = 'name' | 'dateStart' | 'venue' | 'type' | 'status' | 'organizer';
type SortDirection = 'asc' | 'desc';

const EnhancedEventsTable = ({ events, onViewEvent, onEditEvent, onDeleteEvent }: EnhancedEventsTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [organizerFilter, setOrganizerFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('dateStart');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAndFilteredEvents = useMemo(() => {
    let filtered = events.filter(event => {
      const matchesSearch = searchTerm === '' ||
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.venue.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.venue.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = typeFilter === 'all' || event.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
      const matchesOrganizer = organizerFilter === 'all' || 
        (event.organizer?.name && event.organizer.name.toLowerCase().includes(organizerFilter.toLowerCase()));

      return matchesSearch && matchesType && matchesStatus && matchesOrganizer;
    });

    // Sort events
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'dateStart':
          aValue = new Date(a.dateStart);
          bValue = new Date(b.dateStart);
          break;
        case 'venue':
          aValue = `${a.venue.city} ${a.venue.name}`.toLowerCase();
          bValue = `${b.venue.city} ${b.venue.name}`.toLowerCase();
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'status':
          aValue = a.status || 'draft';
          bValue = b.status || 'draft';
          break;
        case 'organizer':
          aValue = a.organizer?.name?.toLowerCase() || '';
          bValue = b.organizer?.name?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [events, searchTerm, typeFilter, statusFilter, organizerFilter, sortField, sortDirection]);

  const uniqueTypes = [...new Set(events.map(event => event.type))];
  const uniqueOrganizers = [...new Set(events.map(event => event.organizer?.name).filter(Boolean))];

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <ArrowUpDown className="h-4 w-4" />
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Keresés események között..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Típus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Minden típus</SelectItem>
            {uniqueTypes.map(type => (
              <SelectItem key={type} value={type}>
                {eventTypeLabels[type as keyof typeof eventTypeLabels] || type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Státusz" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Minden státusz</SelectItem>
            <SelectItem value="draft">Piszkozat</SelectItem>
            <SelectItem value="published">Publikált</SelectItem>
            <SelectItem value="cancelled">Törölve</SelectItem>
          </SelectContent>
        </Select>

        <Select value={organizerFilter} onValueChange={setOrganizerFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Szervező" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Minden szervező</SelectItem>
            {uniqueOrganizers.map(organizer => (
              <SelectItem key={organizer} value={organizer!}>
                {organizer}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {sortedAndFilteredEvents.length} esemény megjelenítve ({events.length} összesen)
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader field="name">Név</SortableHeader>
              <TableHead>Leírás</TableHead>
              <SortableHeader field="dateStart">Dátum</SortableHeader>
              <SortableHeader field="venue">Helyszín</SortableHeader>
              <SortableHeader field="organizer">Szervező</SortableHeader>
              <SortableHeader field="type">Típus</SortableHeader>
              <SortableHeader field="status">Státusz</SortableHeader>
              <TableHead className="w-[100px]">Műveletek</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAndFilteredEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  Nincs esemény a megadott szűrőkkel.
                </TableCell>
              </TableRow>
            ) : (
              sortedAndFilteredEvents.map((event) => (
                <TableRow key={event.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium">
                    {event.name}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">
                    {event.description}
                  </TableCell>
                  <TableCell>
                    {getEventDateRangeString(event.dateStart, event.dateEnd)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{event.venue.city}</div>
                      <div className="text-sm text-muted-foreground">{event.venue.name}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {event.organizer?.name ? (
                      <div>
                        <div className="font-medium">{event.organizer.name}</div>
                        {event.organizer.contactEmail && (
                          <div className="text-sm text-muted-foreground">{event.organizer.contactEmail}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {eventTypeLabels[event.type as keyof typeof eventTypeLabels] || event.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={event.status as EventStatus || EventStatus.DRAFT} />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewEvent(event)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Megtekintés
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEditEvent(event)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Szerkesztés
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDeleteEvent(event)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Törlés
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default EnhancedEventsTable;